// Skins #6 (Bull), #7 (Bear), #8 (Whale), #9 (Rug Puller) builders.

import * as THREE from 'three';
import {
  SEG, darken, lighten, mat, matEmissive, matSkin, enableShadows, buildHumanoid,
  addShortHair,
} from './skin-utils';

/* ------------------------------------------------------------------ */
/*  #6 — The Bull: Human operator in bull-themed tactical gear         */
/* ------------------------------------------------------------------ */

export function buildTheBull(color: number): THREE.Group {
  const group = new THREE.Group();
  const armorColor = 0x2a4a2a; // dark military green
  const skinColor = 0xc8a882;

  const body = buildHumanoid({
    bodyColor: armorColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.5,
    torsoScaleZ: 0.85,
    torsoWidth: 0.21,
    armColor: armorColor,
    legColor: darken(armorColor, 0.8),
    bootColor: darken(armorColor, 0.4),
    handColor: darken(armorColor, 0.5),
    shoulderWidth: 0.34,
    muscular: true,
    eyeColor: 0x446633,
  });
  group.add(body);

  // Bull-horn helmet — human wearing horned tactical helmet
  const helmetBase = new THREE.Mesh(
    new THREE.SphereGeometry(0.165, SEG, 10, 0, Math.PI * 2, 0, Math.PI * 0.55),
    mat(darken(armorColor, 0.5)),
  );
  helmetBase.position.set(0, 1.74, 0);
  group.add(helmetBase);

  // TWO PROMINENT HORNS on helmet
  for (const side of [-1, 1]) {
    const horn = new THREE.Mesh(
      new THREE.ConeGeometry(0.035, 0.22, 8),
      mat(0xe8d8b0, { roughness: 0.35, metalness: 0.25 }),
    );
    horn.position.set(side * 0.14, 1.84, 0.02);
    horn.rotation.z = side * -0.55;
    horn.rotation.x = -0.2;
    group.add(horn);
  }

  // Tactical face shield (mouth guard)
  const faceShield = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.05, 0.04), mat(darken(armorColor, 0.4)));
  faceShield.position.set(0, 1.56, -0.13);
  group.add(faceShield);

  // Green bull-market emblem on chest
  const emblem = new THREE.Mesh(
    new THREE.CylinderGeometry(0.05, 0.05, 0.015, 10),
    mat(color, { roughness: 0.3, metalness: 0.4 }),
  );
  emblem.position.set(0, 1.26, -0.19);
  emblem.rotation.x = Math.PI / 2;
  group.add(emblem);
  // Bull silhouette on emblem (small up-arrow = bull market)
  const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.04, 3), mat(0xffffff));
  arrow.position.set(0, 1.26, -0.21);
  arrow.rotation.x = Math.PI / 2;
  group.add(arrow);

  // Heavy-duty shoulder pads
  for (const side of [-1, 1]) {
    const pad = new THREE.Mesh(
      new THREE.SphereGeometry(0.065, 8, 8),
      mat(darken(armorColor, 0.6)),
    );
    pad.position.set(side * 0.30, 1.44, 0);
    pad.scale.set(1.2, 0.6, 1.0);
    group.add(pad);
  }

  // Tactical belt
  const belt = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.21, 0.05, SEG), mat(darken(armorColor, 0.4)));
  belt.position.set(0, 0.88, 0);
  belt.scale.set(1, 1, 0.65);
  group.add(belt);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #7 — The Bear: Human operator in bear-themed heavy armor           */
/* ------------------------------------------------------------------ */

export function buildTheBear(color: number): THREE.Group {
  const group = new THREE.Group();
  const armorColor = 0x3d2b1f; // dark brown
  const skinColor = 0xc8a882;

  const body = buildHumanoid({
    bodyColor: armorColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.6,
    torsoScaleZ: 0.95,
    torsoWidth: 0.22,
    torsoHeight: 0.35,
    armColor: armorColor,
    legColor: darken(armorColor, 0.8),
    bootColor: darken(armorColor, 0.4),
    handColor: darken(armorColor, 0.5),
    shoulderWidth: 0.36,
    muscular: true,
    eyeColor: 0x553311,
  });
  group.add(body);

  // Bear-ear tactical hood/helmet
  const bearHelmet = new THREE.Mesh(
    new THREE.SphereGeometry(0.175, SEG, 10, 0, Math.PI * 2, 0, Math.PI * 0.6),
    mat(darken(armorColor, 0.7)),
  );
  bearHelmet.position.set(0, 1.74, 0.01);
  group.add(bearHelmet);

  // Bear ears on hood
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 8), mat(armorColor));
    ear.position.set(side * 0.12, 1.88, 0.02);
    group.add(ear);
    const innerEar = new THREE.Mesh(new THREE.SphereGeometry(0.028, 6, 6), mat(darken(armorColor, 0.6)));
    innerEar.position.set(side * 0.12, 1.88, -0.02);
    group.add(innerEar);
  }

  // Bear-face visor/mask over lower face
  const bearMask = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 8, 8),
    mat(lighten(armorColor, 1.2)),
  );
  bearMask.position.set(0, 1.58, -0.10);
  bearMask.scale.set(1.0, 0.8, 0.7);
  group.add(bearMask);
  // Nose on mask
  const maskNose = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 6), mat(0x0a0a0a));
  maskNose.position.set(0, 1.60, -0.16);
  group.add(maskNose);

  // Heavy gauntlets (oversized hand armor)
  for (const side of [-1, 1]) {
    const gauntlet = new THREE.Mesh(
      new THREE.SphereGeometry(0.07, 8, 8),
      mat(darken(armorColor, 0.7)),
    );
    gauntlet.position.set(side * 0.28, 0.82, 0);
    group.add(gauntlet);
    // Claw marks on gauntlet
    for (let i = 0; i < 3; i++) {
      const claw = new THREE.Mesh(
        new THREE.ConeGeometry(0.008, 0.04, 4),
        mat(0xcccccc, { metalness: 0.6 }),
      );
      claw.position.set(side * 0.28 + (i - 1) * 0.02, 0.76, -0.04);
      claw.rotation.x = -0.3;
      group.add(claw);
    }
  }

  // Red bear-market stripe on chest
  const redStripe = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.08, 0.015), mat(color));
  redStripe.position.set(0, 1.10, -0.19);
  group.add(redStripe);
  // Down arrow (bear market)
  const downArrow = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.04, 3), mat(0xffffff));
  downArrow.position.set(0, 1.10, -0.21);
  downArrow.rotation.x = -Math.PI / 2;
  downArrow.rotation.z = Math.PI;
  group.add(downArrow);

  // Heavy shoulder pads
  for (const side of [-1, 1]) {
    const pad = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), mat(darken(armorColor, 0.6)));
    pad.position.set(side * 0.32, 1.44, 0);
    pad.scale.set(1.2, 0.6, 1.0);
    group.add(pad);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #8 — The Whale: Opulent suited large character                     */
/* ------------------------------------------------------------------ */

export function buildTheWhale(color: number): THREE.Group {
  const group = new THREE.Group();
  const skinColor = 0xd4b896;

  const body = buildHumanoid({
    bodyColor: color,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.7,
    torsoScaleZ: 1.1,
    torsoWidth: 0.24,
    torsoHeight: 0.36,
    armColor: color,
    legColor: darken(color, 0.8),
    bootColor: 0x111111,
    handColor: skinColor,
    shoulderWidth: 0.36,
    eyeColor: 0x334466,
  });
  group.add(body);

  // Overall wider scale
  group.scale.set(1.12, 1.0, 1.12);

  // Slicked-back silver-streaked hair
  addShortHair(group, 0x444455, 1.66, 0.14);

  // Top hat
  const hatBrim = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.018, SEG), mat(0x0a0a0a));
  hatBrim.position.set(0, 1.82, 0);
  group.add(hatBrim);
  const hatTop = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.12, 0.18, SEG), mat(0x0a0a0a));
  hatTop.position.set(0, 1.93, 0);
  group.add(hatTop);
  // Hat band
  const hatBand = new THREE.Mesh(new THREE.CylinderGeometry(0.122, 0.122, 0.02, SEG), mat(0xffd700, { metalness: 0.6 }));
  hatBand.position.set(0, 1.84, 0);
  group.add(hatBand);

  // Monocle
  const monocle = new THREE.Mesh(
    new THREE.TorusGeometry(0.032, 0.004, 8, 16),
    mat(0xffd700, { roughness: 0.15, metalness: 0.85 }),
  );
  monocle.position.set(-0.06, 1.68, -0.13);
  monocle.rotation.y = 0.1;
  group.add(monocle);
  // Monocle glass
  const monocleGlass = new THREE.Mesh(
    new THREE.CylinderGeometry(0.03, 0.03, 0.003, 12),
    mat(0xccddee, { roughness: 0.05, metalness: 0.3 }),
  );
  monocleGlass.position.set(-0.06, 1.68, -0.13);
  monocleGlass.rotation.x = Math.PI / 2;
  group.add(monocleGlass);
  // Chain
  const chain = new THREE.Mesh(new THREE.CylinderGeometry(0.002, 0.002, 0.14, 4), mat(0xffd700, { metalness: 0.8 }));
  chain.position.set(-0.08, 1.58, -0.12);
  chain.rotation.z = 0.3;
  group.add(chain);

  // Suit lapels
  for (const side of [-1, 1]) {
    const lapel = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.14, 0.02),
      mat(darken(color, 0.6)),
    );
    lapel.position.set(side * 0.10, 1.34, -0.17);
    lapel.rotation.z = side * 0.15;
    group.add(lapel);
  }
  // Tie
  const tie = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.18, 0.015), mat(0xaa0000));
  tie.position.set(0, 1.30, -0.18);
  group.add(tie);

  // Gold belt
  const belt = new THREE.Mesh(
    new THREE.CylinderGeometry(0.28, 0.27, 0.05, SEG),
    mat(0xffd700, { roughness: 0.25, metalness: 0.7 }),
  );
  belt.position.set(0, 0.88, 0);
  belt.scale.set(1, 1, 0.65);
  group.add(belt);

  // Cigar
  const cigar = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.013, 0.11, 8), mat(0x5c3a1e));
  cigar.position.set(0.10, 1.56, -0.15);
  cigar.rotation.z = 0.35;
  cigar.rotation.x = -0.15;
  group.add(cigar);
  const ember = new THREE.Mesh(new THREE.SphereGeometry(0.012, 6, 6), matEmissive(0xff4400, 1.5));
  ember.position.set(0.14, 1.58, -0.17);
  group.add(ember);

  // Gold cuff links
  for (const side of [-1, 1]) {
    const cuff = new THREE.Mesh(
      new THREE.CylinderGeometry(0.045, 0.045, 0.025, 8),
      mat(0xffd700, { roughness: 0.25, metalness: 0.7 }),
    );
    cuff.position.set(side * 0.28, 0.98, 0);
    group.add(cuff);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #9 — Rug Puller: Hooded trickster pulling the rug                  */
/* ------------------------------------------------------------------ */

export function buildRugPuller(color: number): THREE.Group {
  const group = new THREE.Group();
  const robeColor = darken(color, 0.5);
  const skinColor = 0xc0a888;

  const body = buildHumanoid({
    bodyColor: robeColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.05,
    torsoScaleZ: 0.65,
    armColor: robeColor,
    legColor: darken(robeColor, 0.8),
    bootColor: 0x1a1a1a,
    handColor: skinColor,
    showFace: false, // hidden under hood
  });
  group.add(body);

  // Deep hood — cone/dome over head
  const hood = new THREE.Mesh(
    new THREE.ConeGeometry(0.22, 0.30, SEG),
    mat(darken(color, 0.35)),
  );
  hood.position.set(0, 1.84, 0.03);
  group.add(hood);
  // Hood rim
  const hoodRim = new THREE.Mesh(
    new THREE.TorusGeometry(0.16, 0.015, 6, 12, Math.PI),
    mat(darken(color, 0.3)),
  );
  hoodRim.position.set(0, 1.72, -0.06);
  hoodRim.rotation.x = 0.3;
  group.add(hoodRim);

  // Dark face shadow in hood
  const faceShadow = new THREE.Mesh(new THREE.SphereGeometry(0.11, SEG, SEG), mat(0x0a0a0a));
  faceShadow.position.set(0, 1.65, -0.04);
  group.add(faceShadow);

  // Glowing green eyes visible in shadow — signature feature
  for (const side of [-0.04, 0.04]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.018, 8, 8), matEmissive(0x00ff00, 2.5));
    eye.position.set(side, 1.67, -0.13);
    group.add(eye);
  }

  // Shadowy mouth hint
  const mouth = new THREE.Mesh(
    new THREE.TorusGeometry(0.025, 0.004, 4, 8, Math.PI),
    matEmissive(0x008800, 0.5),
  );
  mouth.position.set(0, 1.59, -0.12);
  mouth.rotation.x = Math.PI;
  group.add(mouth);

  // Snake scale pattern on robe
  for (let i = 0; i < 6; i++) {
    const diamond = new THREE.Mesh(
      new THREE.BoxGeometry(0.035, 0.035, 0.015),
      mat(0x00aa00),
    );
    diamond.position.set(
      (i % 2 === 0 ? 0.05 : -0.05),
      1.28 - i * 0.06,
      -0.18,
    );
    diamond.rotation.z = Math.PI / 4;
    group.add(diamond);
  }

  // One hand pulling the "rug" — flat rectangle
  const rug = new THREE.Mesh(
    new THREE.BoxGeometry(0.28, 0.018, 0.18),
    mat(0xaa2222),
  );
  rug.position.set(0.34, 0.82, -0.08);
  rug.rotation.z = -0.12;
  group.add(rug);
  // Rug pattern
  const rugPattern = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.02, 0.12),
    mat(0xcc8833),
  );
  rugPattern.position.set(0.34, 0.83, -0.08);
  rugPattern.rotation.z = -0.12;
  group.add(rugPattern);

  // Fringe
  for (let i = 0; i < 4; i++) {
    const fringe = new THREE.Mesh(new THREE.BoxGeometry(0.015, 0.04, 0.012), mat(0xcc8833));
    fringe.position.set(0.20 + i * 0.055, 0.80, -0.08);
    group.add(fringe);
  }

  enableShadows(group);
  return group;
}
